import React, { useEffect } from 'react';
import Footer from '../components/Footer';
import SiteNavigation from '../components/SiteNavigation';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 120; // Account for fixed header
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="site-canvas legal-page">
      <SiteNavigation currentPage="privacy" />

      {/* Header */}
      <header className="border-b border-slate-200/80 pt-16">
        <div className="site-container py-10 sm:py-12">
          <p className="section-kicker">Legal</p>
          <h1 className="section-title">
            Privacy Policy
          </h1>
          <p className="section-copy mt-3">
            1Medicine — Privacy Policy
          </p>
        </div>
      </header>

      <main className="site-container py-10 sm:py-14">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[15rem_minmax(0,1fr)]">
          {/* Table of Contents */}
          <aside className="surface-card p-5 lg:sticky lg:top-24" aria-labelledby="privacy-contents-heading">
              <h2 id="privacy-contents-heading" className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-900">Table of Contents</h2>
              <nav className="space-y-1" aria-label="Privacy policy sections">
                <button
                  onClick={() => scrollToSection('introduction')}
                  className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                >
                  Introduction
                </button>
                <button
                  onClick={() => scrollToSection('information-collected')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  1. Information We Collect
                </button>
                <button
                  onClick={() => scrollToSection('how-we-use')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  2. How We Use Your Information
                </button>
                <button
                  onClick={() => scrollToSection('how-we-share')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  3. How We Share Your Information
                </button>
                <button
                  onClick={() => scrollToSection('data-security')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  4. Data Security
                </button>
                <button
                  onClick={() => scrollToSection('data-retention')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  5. Data Retention
                </button>
                <button
                  onClick={() => scrollToSection('your-rights')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  6. Your Rights and Choices
                </button>
                <button
                  onClick={() => scrollToSection('children-privacy')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  7. Children's Privacy
                </button>
                <button
                  onClick={() => scrollToSection('changes')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  8. Changes to This Privacy Policy
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  9. Contact Us
                </button>
              </nav>
          </aside>

          {/* Main Content */}
          <article className="surface-card min-w-0 p-6 sm:p-8 lg:p-10">
            <div className="prose max-w-none">
              {/* Introduction */}
              <section id="introduction" className="mb-12">
                <p className="deepseek-text leading-relaxed mb-6 text-lg">
                  At 1Medicine, operated by Red Canvas LLC, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you use our application (the "App"). By using 1Medicine, you consent to the practices described in this policy.
                </p>
              </section>

              {/* Section 1: Information We Collect */}
              <section id="information-collected" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">1. Information We Collect</h2>
                <p className="deepseek-text leading-relaxed mb-4">
                  When you use our App, we may collect the following types of information:
                </p>
                <div className="space-y-4">
                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">Personal Information</h3>
                    <p className="deepseek-text">
                      When you create an account, make a purchase, or contact us, we may collect personal information such as your name, email address, payment information, and other details you provide.
                    </p>
                  </div>
                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">Usage Data</h3>
                    <p className="deepseek-text">
                      We automatically collect certain information about your interaction with the App, including IP address, device information, operating system, and usage patterns.
                    </p>
                  </div>
                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">Content Data</h3>
                    <p className="deepseek-text">
                      If you upload, submit, or share content through the App, we may collect and store this content as part of our services.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 2: How We Use Your Information */}
              <section id="how-we-use" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">2. How We Use Your Information</h2>
                <p className="deepseek-text leading-relaxed mb-4">
                  Red Canvas LLC uses the collected information for various purposes, including:
                </p>
                <ul className="list-disc list-inside space-y-2 deepseek-text ml-4">
                  <li>To provide, maintain, and improve the App's features and functionality.</li>
                  <li>To process transactions and manage your account, including payments and subscriptions.</li>
                  <li>To communicate with you regarding updates, offers, and other information related to 1Medicine.</li>
                  <li>To monitor and analyze trends, usage, and activities in connection with our App.</li>
                  <li>To enforce our Terms and Conditions and protect the security of the App and our users.</li>
                </ul>
              </section>

              {/* Section 3: How We Share Your Information */}
              <section id="how-we-share" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">3. How We Share Your Information</h2>
                <p className="deepseek-text leading-relaxed mb-4">
                  Red Canvas LLC may share your personal information in the following situations:
                </p>
                <div className="space-y-4">
                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">With Service Providers</h3>
                    <p className="deepseek-text">
                      We may share your information with trusted third-party service providers to help us operate the App, such as payment processors and customer support providers.
                    </p>
                  </div>
                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">For Legal Reasons</h3>
                    <p className="deepseek-text">
                      We may disclose your information if required by law, to comply with legal processes, or to protect the rights and safety of Red Canvas LLC, our users, and the public.
                    </p>
                  </div>
                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">With Your Consent</h3>
                    <p className="deepseek-text">
                      We may share your information for other purposes with your explicit consent.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 4: Data Security */}
              <section id="data-security" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">4. Data Security</h2>
                <p className="deepseek-text leading-relaxed">
                  We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, please be aware that no data transmission over the internet or electronic storage method is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              {/* Section 5: Data Retention */}
              <section id="data-retention" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">5. Data Retention</h2>
                <p className="deepseek-text leading-relaxed">
                  Red Canvas LLC retains your personal information only as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
                </p>
              </section>

              {/* Section 6: Your Rights and Choices */}
              <section id="your-rights" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">6. Your Rights and Choices</h2>
                <p className="deepseek-text leading-relaxed mb-4">
                  You have certain rights regarding your personal information, including:
                </p>
                <ul className="list-disc list-inside space-y-2 deepseek-text ml-4">
                  <li><strong>Access and Correction:</strong> You can access, update, or correct your personal information by logging into your account or contacting us directly.</li>
                  <li><strong>Data Portability:</strong> You may have the right to request a copy of your personal information in a structured, commonly used, and machine-readable format.</li>
                  <li><strong>Deletion:</strong> You may request the deletion of your personal information, subject to certain legal limitations.</li>
                  <li><strong>Opt-Out of Communications:</strong> You can opt out of receiving promotional emails from us by following the unsubscribe link in the email or contacting us directly.</li>
                </ul>
              </section>

              {/* Section 7: Children's Privacy */}
              <section id="children-privacy" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">7. Children's Privacy</h2>
                <p className="deepseek-text leading-relaxed">
                  1Medicine is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected information from a child under 13, we will promptly delete such information from our records.
                </p>
              </section>

              {/* Section 8: Changes to This Privacy Policy */}
              <section id="changes" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">8. Changes to This Privacy Policy</h2>
                <p className="deepseek-text leading-relaxed">
                  Red Canvas LLC may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of the App after any changes constitute acceptance of the new Privacy Policy.
                </p>
              </section>

              {/* Section 9: Contact Us */}
              <section id="contact" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">9. Contact Us</h2>
                <p className="deepseek-text leading-relaxed mb-4">
                  If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
                </p>
                <div className="deepseek-card rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">Red Canvas LLC</h3>
                  <div className="space-y-2 deepseek-text">
                    <p>18117 Biscayne Blvd #1136</p>
                    <p>Miami, FL 33160, USA</p>
                    <p>Email: <a href="mailto:contact@1medicine.app" className="text-blue-400 hover:text-blue-300">contact@1medicine.app</a></p>
                  </div>
                </div>
                <p className="deepseek-text leading-relaxed mt-6">
                  By using 1Medicine, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
                </p>
              </section>
            </div>
          </article>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;